import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import _ from 'underscore';

import {Batch} from 'constants/SampleConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {
  isFulfilmentDefinitionSelected,
  toggleFulfillmentDefinitionSelection,
  toggleHeaderCheckboxSelection,
} from 'requests/execution/FulfillmentDefinitionSelectionUtil';
import {
  FULFILLMENT_STATUS_NAMES,
  FulfillmentStatus,
  RequestFulfillment,
  RequestFulfillmentDefinition,
} from 'requests/fulfillments/FulfillmentConstants';
import FulfillmentStatusFilterDropdown from 'requests/fulfillments/FulfillmentStatusFilterDropdown';
import FulfillmentStatusIndicator from 'requests/fulfillments/FulfillmentStatusIndicator';
import {fetchRequestSampleGroups} from 'requests/inputs/InputReduxActions';
import {getLegacyRequestObjectName, Request, RequestSchema} from 'requests/RequestConstants';
import RequestSampleCell from 'requests/RequestSampleCell';
import {RequestTask, RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import StorableStore from 'samples/StorableStore';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BLUE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import prepareSampleGroupsForTable from 'workflows/samples/prepareSampleGroupsForTable';
import {SampleGroup, SampleGroupSchema} from 'workflows/WorkflowConstants';

import DataTestAttrs from '../../test/lib/cypress/shared/DataTestAttrs';
import {aggregateFulfillmentStatuses} from '../fulfillments/FulfillmentStatus';

function getTaskSchema(
  fulfillmentDefinition: RequestFulfillmentDefinition,
  requestTasksById: {[taskId: string]: RequestTask},
  requestTaskSchemasById: {[schemaId: string]: RequestTaskSchema}
): RequestTaskSchema {
  const requestTask = requestTasksById[fulfillmentDefinition.requestTaskId!];
  return requestTaskSchemasById[requestTask.schemaId];
}

interface DefaultBaseStaticSampleGroupTableProps {
  allowSelect: boolean;
}

interface BaseStaticSampleGroupTableProps {
  allowSelect?: boolean;
  fulfillmentDefinitions: Array<RequestFulfillmentDefinition>;
  selectedFulfillmentDefinitions: Array<RequestFulfillmentDefinition>;
  requestSchemasByRequestId: {[requestId: string]: RequestSchema};
  statusFilter: FulfillmentStatus | null;
  onSetStatusFilter: (statusFilter: FulfillmentStatus) => void;
  onSetSelectedFulfillmentDefinitions: (
    selectedFulfillmentDefinitions: Array<RequestFulfillmentDefinition>
  ) => void;
  dataTestAttrs?: DataTestAttrs;
}

interface ConnectedProps {
  existingFulfillmentsByFulfillmentDefinitionId: {[definitionId: string]: Array<RequestFulfillment>} | null;
  requestsByFulfillmentDefinitionId: {[definitionId: string]: Request} | null;
  requestTasksById: {[taskId: string]: RequestTask} | null;
  sampleGroupsById: {[sampleGroupId: string]: SampleGroup} | null;
  requestTaskSchemasById: {[schemaId: string]: RequestTaskSchema} | null;
}

type StaticSampleGroupTableProps = BaseStaticSampleGroupTableProps & ConnectedProps;

class StaticSampleGroupTable extends React.Component<StaticSampleGroupTableProps> {
  static defaultProps: DefaultBaseStaticSampleGroupTableProps = {
    allowSelect: true,
  };

  _isFulfillmentDefinitionSelected = (fulfillmentDefinition: RequestFulfillmentDefinition): boolean =>
    isFulfilmentDefinitionSelected(fulfillmentDefinition, this.props.selectedFulfillmentDefinitions);

  _handleRowClick = (rowIndex: number): void => {
    this.props.onSetSelectedFulfillmentDefinitions(
      toggleFulfillmentDefinitionSelection(
        this.props.fulfillmentDefinitions[rowIndex],
        this.props.selectedFulfillmentDefinitions
      )
    );
  };

  _hasTasks(): boolean {
    return this.props.fulfillmentDefinitions.some((definition) => definition.requestTaskId != null);
  }

  _handleToggleHeaderCheckbox = (): void => {
    this.props.onSetSelectedFulfillmentDefinitions(
      toggleHeaderCheckboxSelection(
        this.props.fulfillmentDefinitions,
        this.props.selectedFulfillmentDefinitions
      )
    );
  };

  _renderSampleGroupColumn(sampleGroupSchema: SampleGroupSchema, field: SchemaField): JSX.Element {
    return (
      <Column
        key={`${sampleGroupSchema.id},${field.name}`}
        header={field.displayName}
        renderCell={(rowIndex: number) => (
          <Cell>
            {/* eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing */}
            {(() => {
              const fulfillmentDefinition = this.props.fulfillmentDefinitions[rowIndex];
              const fulfillmentSampleGroupSchema =
                this.props.requestSchemasByRequestId[fulfillmentDefinition.requestId].sampleGroupSchema!;
              if (fulfillmentSampleGroupSchema.id !== sampleGroupSchema.id) {
                return null;
              }
              const sampleGroup = this.props.sampleGroupsById![fulfillmentDefinition.sampleGroupId!];
              const tag = sampleGroup.tags.find(({name}) => name === field.name)!;
              return <RequestSampleCell samples={tag.targetSamples!} tagSchemaField={field} />;
            })()}
          </Cell>
        )}
      />
    );
  }

  _getOrderedSampleGroupSchemas(): Array<SampleGroupSchema> {
    const sampleGroupSchemas = _.chain(this.props.fulfillmentDefinitions)
      .pluck('requestId')
      .map((requestId) => this.props.requestSchemasByRequestId[requestId].sampleGroupSchema)
      .compact()
      .value() as Array<SampleGroupSchema>;
    return _.chain(sampleGroupSchemas)
      .unique((sampleGroupSchema) => sampleGroupSchema.id)
      .sortBy((sampleGroupSchema) => sampleGroupSchema.id)
      .value();
  }

  _renderSampleGroupColumns(): Array<JSX.Element> {
    const sampleGroupSchemas = this._getOrderedSampleGroupSchemas();
    const columns = [];
    for (const sampleGroupSchema of sampleGroupSchemas) {
      for (const field of sampleGroupSchema.fields) {
        columns.push(this._renderSampleGroupColumn(sampleGroupSchema, field));
      }
    }
    return columns;
  }

  render(): JSX.Element {
    return (
      <Table
        emptyTableText="No samples"
        numRows={this.props.fulfillmentDefinitions.length}
        onRowClick={this._handleRowClick}
      >
        {this.props.fulfillmentDefinitions.length > 0 && this.props.allowSelect && (
          <Column
            header={
              <Cell className="simpleTable-checkboxCell">
                <IndeterminateCheckbox
                  items={this.props.fulfillmentDefinitions}
                  selectedMap={this._isFulfillmentDefinitionSelected}
                  onChange={this._handleToggleHeaderCheckbox}
                />
              </Cell>
            }
            renderCell={(rowIndex: number) => (
              <Cell className="simpleTable-checkboxCell">
                <input
                  type="checkbox"
                  className={css(styles.checkbox)}
                  checked={this._isFulfillmentDefinitionSelected(this.props.fulfillmentDefinitions[rowIndex])}
                  onChange={() => {
                    this._handleRowClick(rowIndex);
                  }}
                />
              </Cell>
            )}
          />
        )}
        {this._renderSampleGroupColumns()}
        <Column
          header={`${getLegacyRequestObjectName()} ID`}
          renderCell={(rowIndex: number) => (
            <Cell>
              {(() => {
                const fulfillmentDefinition = this.props.fulfillmentDefinitions[rowIndex];
                return this.props.requestsByFulfillmentDefinitionId![fulfillmentDefinition.cid].name;
              })()}
            </Cell>
          )}
        />
        {this._hasTasks() && (
          <Column
            header="Task"
            renderCell={(rowIndex: number) => (
              <Cell>
                {(() => {
                  const fulfillmentDefinition = this.props.fulfillmentDefinitions[rowIndex];
                  if (fulfillmentDefinition.requestTaskId == null) {
                    return null;
                  }
                  return getTaskSchema(
                    fulfillmentDefinition,
                    this.props.requestTasksById!,
                    this.props.requestTaskSchemasById!
                  ).name;
                })()}
              </Cell>
            )}
          />
        )}
        <Column
          header={
            <Cell className="u-vertical-center-items">
              Status
              <div className="u-mini-spacing-left">
                <FulfillmentStatusFilterDropdown
                  statusOptions={Object.values(FulfillmentStatus)}
                  value={this.props.statusFilter}
                  onSelect={this.props.onSetStatusFilter}
                />
              </div>
            </Cell>
          }
          renderCell={(rowIndex: number) => {
            const fulfillmentDefinition = this.props.fulfillmentDefinitions[rowIndex];
            const fulfillments =
              this.props.existingFulfillmentsByFulfillmentDefinitionId![fulfillmentDefinition.cid];
            const aggregateStatus = aggregateFulfillmentStatuses(fulfillments);
            return (
              <Cell>
                <FulfillmentStatusIndicator
                  className="u-display-inline-block u-valign-middle"
                  status={aggregateStatus}
                />
                <span className="u-mini-spacing-left u-valign-middle">
                  {FULFILLMENT_STATUS_NAMES[aggregateStatus]}
                </span>
              </Cell>
            );
          }}
        />
      </Table>
    );
  }
}

interface BaseSampleGroupTableProps extends BaseStaticSampleGroupTableProps {
  requestIds: Array<string>;
  selectedTaskSchemaId: string | null;
}

class SampleGroupTable extends React.Component<
  BaseSampleGroupTableProps & {batchesById: {[batchId: string]: Batch}} & ConnectedProps
> {
  static defaultProps: DefaultBaseStaticSampleGroupTableProps = {
    allowSelect: true,
  };

  _isFulfillmentDefinitionMissingData(definition: RequestFulfillmentDefinition): boolean {
    return (
      (definition.requestTaskId != null && this.props.requestTasksById![definition.requestTaskId] == null) ||
      (definition.sampleGroupId != null && this.props.sampleGroupsById![definition.sampleGroupId] == null) ||
      (definition.requestTaskId != null &&
        getTaskSchema(definition, this.props.requestTasksById!, this.props.requestTaskSchemasById!) == null)
    );
  }

  _isLoading(): boolean {
    const nullableProps = [
      this.props.fulfillmentDefinitions,
      this.props.selectedFulfillmentDefinitions,
      this.props.existingFulfillmentsByFulfillmentDefinitionId,
      this.props.requestsByFulfillmentDefinitionId,
      this.props.requestTasksById,
      this.props.sampleGroupsById,
      this.props.requestTaskSchemasById,
    ];

    return (
      nullableProps.some((prop) => prop == null) ||
      this.props.fulfillmentDefinitions.some((definition) =>
        this._isFulfillmentDefinitionMissingData(definition)
      )
    );
  }

  _filterByStatus(
    fulfillmentDefinitions: Array<RequestFulfillmentDefinition>
  ): Array<RequestFulfillmentDefinition> {
    if (this.props.statusFilter == null) {
      return fulfillmentDefinitions;
    }

    return fulfillmentDefinitions.filter((definition) => {
      const existingFulfillments = this.props.existingFulfillmentsByFulfillmentDefinitionId![definition.cid];
      const aggregateStatus = aggregateFulfillmentStatuses(existingFulfillments);
      return aggregateStatus === this.props.statusFilter;
    });
  }

  _filterByTaskSchema(
    fulfillmentDefinitions: Array<RequestFulfillmentDefinition>
  ): Array<RequestFulfillmentDefinition> {
    if (this.props.selectedTaskSchemaId == null) {
      return fulfillmentDefinitions;
    }

    return fulfillmentDefinitions.filter((definition) => {
      const task =
        definition.requestTaskId == null ? null : this.props.requestTasksById![definition.requestTaskId];
      return task != null && task.schemaId === this.props.selectedTaskSchemaId;
    });
  }

  render(): JSX.Element {
    const {
      fulfillmentDefinitions,
      selectedFulfillmentDefinitions,
      statusFilter,
      existingFulfillmentsByFulfillmentDefinitionId,
      requestsByFulfillmentDefinitionId,
      requestTasksById,
      requestSchemasByRequestId,
      requestTaskSchemasById,
      sampleGroupsById,
      onSetStatusFilter,
      onSetSelectedFulfillmentDefinitions,
    } = this.props;

    if (this._isLoading()) {
      return <CenteredHorizontalThrobber />;
    }

    return (
      <StaticSampleGroupTable
        allowSelect={this.props.allowSelect}
        fulfillmentDefinitions={this._filterByStatus(this._filterByTaskSchema(fulfillmentDefinitions))}
        selectedFulfillmentDefinitions={selectedFulfillmentDefinitions}
        existingFulfillmentsByFulfillmentDefinitionId={existingFulfillmentsByFulfillmentDefinitionId}
        requestsByFulfillmentDefinitionId={requestsByFulfillmentDefinitionId}
        requestTasksById={requestTasksById}
        requestSchemasByRequestId={requestSchemasByRequestId}
        requestTaskSchemasById={requestTaskSchemasById}
        sampleGroupsById={sampleGroupsById}
        statusFilter={statusFilter}
        onSetStatusFilter={onSetStatusFilter}
        onSetSelectedFulfillmentDefinitions={onSetSelectedFulfillmentDefinitions}
      />
    );
  }
}

function mapStateToProps(
  state: BenchlingState,
  {
    requestIds,
    fulfillmentDefinitions,
    batchesById,
  }: BaseSampleGroupTableProps & {batchesById: {[batchId: string]: Batch}}
): ConnectedProps {
  const stageRunSamplesById = new WorkflowSelector(state.models.workflows).getStageRunSampleById();
  const {requestsById, requestTasksById} = state.models.requests.inputs;
  const {requestTaskSchemasById} = state.models.requests.schemas;

  const {fulfillmentIdsByRequestId, fulfillmentsById} = state.models.requests.fulfillments;
  const {fileSummariesById} = state.models;
  const {sampleGroupsById} = state.models.sampleGroups;

  if (
    fulfillmentDefinitions == null ||
    fulfillmentIdsByRequestId == null ||
    requestsById == null ||
    requestTasksById == null ||
    fulfillmentsById == null ||
    requestTaskSchemasById == null ||
    stageRunSamplesById == null ||
    sampleGroupsById == null
  ) {
    return {
      existingFulfillmentsByFulfillmentDefinitionId: null,
      requestsByFulfillmentDefinitionId: null,
      requestTasksById: null,
      sampleGroupsById: null,
      requestTaskSchemasById: null,
    };
  }

  const requestsByFulfillmentDefinitionId: {[definitionId: string]: Request} = {};
  const existingFulfillmentsByFulfillmentDefinitionId: {
    [definitionId: string]: Array<RequestFulfillment>;
  } = {};

  // eslint-disable-next-line no-restricted-syntax
  const existingFulfillments = _.flatten(requestIds.map((id) => fulfillmentIdsByRequestId[id]));
  for (const fulfillmentDefinition of fulfillmentDefinitions) {
    requestsByFulfillmentDefinitionId[fulfillmentDefinition.cid] =
      requestsById[fulfillmentDefinition.requestId];
    existingFulfillmentsByFulfillmentDefinitionId[fulfillmentDefinition.cid] = existingFulfillments
      .map((fulfillmentId) => fulfillmentsById[fulfillmentId])
      .filter(
        (fulfillment) =>
          fulfillment.requestId === fulfillmentDefinition.requestId &&
          fulfillment.sampleGroupId === fulfillmentDefinition.sampleGroupId &&
          fulfillment.requestTaskId === fulfillmentDefinition.requestTaskId
      );
  }

  const storeState = {batchesById, fileSummariesById, stageRunSamplesById};
  const preparedSampleGroupsArray = prepareSampleGroupsForTable(storeState, Object.values(sampleGroupsById));
  const preparedSampleGroupsById = _.indexBy(preparedSampleGroupsArray, 'id');

  return {
    existingFulfillmentsByFulfillmentDefinitionId,
    requestsByFulfillmentDefinitionId,
    requestTasksById,
    sampleGroupsById: preparedSampleGroupsById,
    requestTaskSchemasById,
  };
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default compose(
  StorableStore.connect((storableStore: typeof StorableStore) => ({batchesById: storableStore.getBatches()})),
  connect(mapStateToProps, {fetchRequestSampleGroups})
)(SampleGroupTable) as React.ComponentClass<BaseSampleGroupTableProps>;
